import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Chip } from '@mui/material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { BuyerOrderService } from '../../services/buyerOrder.service';
import { AuthService } from '../../services/auth.service';
import { RecurringJournal, RecurringJournalRun } from '../../interfaces/recurringJournal.interface';
import { RecurringJournalService } from '../../services/recurring-journal.service';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import ScrollableTable from '../common/ScrollableTable';
import { formatDate } from '../../utils/dateUtils';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { countDueOccurrences, describeSchedule, statusColor } from './recurringJournalText';
import { toLocalIsoDate } from '../../utils/dateUtils';
import { departmentLabel, useDepartmentOptions } from '../costcentre/DepartmentPicker'; // B4-H
import { projectLabel, useProjectOptions } from '../project/ProjectSelect'; // P1-A project

const th = { padding: '12px', textAlign: 'left' as const, backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd' };
const td = { padding: '12px', borderBottom: '1px solid #ddd', verticalAlign: 'top' as const };

const RecurringJournalDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const toast = useToast();
    const canManageBase = usePermission(PERMISSIONS.JOURNAL_ENTRY_MANAGE);
    const canPost = usePermission(PERMISSIONS.JOURNAL_ENTRY_POST);
    const [t, setT] = useState<RecurringJournal | null>(null);
    const [runs, setRuns] = useState<RecurringJournalRun[]>([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [orderNumbers, setOrderNumbers] = useState<Record<string, string>>({});   // polish 1
    const departmentOptions = useDepartmentOptions(AuthService.getSelectedInstitution()); // B4-H
    const departmentNames = Object.fromEntries(departmentOptions.map(d => [d.id, departmentLabel(d)])) as Record<string, string>;
    const projectOptions = useProjectOptions(AuthService.getSelectedInstitution()); // P1-A project
    const projectNames = Object.fromEntries(projectOptions.map(p => [p.id, projectLabel(p)])) as Record<string, string>;
    const [banner, setBanner] = useState<{ severity: 'success' | 'warning'; text: string } | null>(null);

    const load = useCallback(async () => {
        if (!id) return;
        try {
            const [tpl, r] = await Promise.all([RecurringJournalService.get(id), RecurringJournalService.runs(id)]);
            setT(tpl);
            setRuns(r);
        } catch (err) {
            toast.showApiError(err, 'Failed to load recurring journal');
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => { load(); }, [load]);

    // Polish 1: resolve the numbers of the orders the template lines are charged to (best effort; falls back to the id).
    useEffect(() => {
        const institutionId = AuthService.getSelectedInstitution();
        const ids = Array.from(new Set((t?.lines ?? []).map(l => l.buyerOrderId).filter((x): x is string => !!x)));
        if (!institutionId || ids.length === 0) return;
        ids.forEach(oid => {
            Promise.resolve(BuyerOrderService.getById(institutionId, oid))
                .then(o => setOrderNumbers(prev => ({ ...prev, [oid]: o.orderNumber })))
                .catch(() => undefined);
        });
    }, [t]);

    const act = async (fn: () => Promise<unknown>, okMsg: string, errMsg: string) => {
        try {
            setBusy(true);
            await fn();
            toast.showSuccess(okMsg);
            await load();
        } catch (err) {
            toast.showApiError(err, errMsg);
        } finally {
            setBusy(false);
        }
    };

    const runNow = async () => {
        if (!id) return;
        try {
            setBusy(true);
            const res = await RecurringJournalService.runNow(id);
            setBanner({ severity: res.failed > 0 || res.capped ? 'warning' : 'success', text: res.message });
            await load();
        } catch (err) {
            toast.showApiError(err, 'Failed to run recurring journal');
        } finally {
            setBusy(false);
        }
    };

    if (loading) return <div>Loading...</div>;
    if (!t || !id) return <div style={{ color: 'red', padding: 20 }}>Recurring journal not found</div>;

    // A POST-mode template posts unattended: only people who may post can run, edit or retry it.
    const canManage = canManageBase;
    const canOperate = canManageBase && (t.mode !== 'POST' || canPost);
    const editable = t.status === 'ACTIVE' || t.status === 'PAUSED';
    const hasOrders = t.lines.some(l => !!l.buyerOrderId);
    const hasDepartments = t.lines.some(l => !!l.departmentId); // B4-H
    const hasProjects = t.lines.some(l => !!l.projectId); // P1-A project
    return (
        <div data-help-page="accounting.recurringDetail.page" style={{ padding: '20px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h2>{t.name}</h2>
                <Button variant="outlined" onClick={() => navigate('/accounting/recurring-journals')}>Back to list</Button>
            </div>
            {banner && <Alert severity={banner.severity} sx={{ mb: 2 }} onClose={() => setBanner(null)}>{banner.text}</Alert>}

            <div style={{ marginBottom: 20, padding: 16, backgroundColor: '#f9f9f9', borderRadius: 8, display: 'flex', gap: 32, flexWrap: 'wrap' }}>
                <div><strong>Schedule:</strong> {describeSchedule(t)}</div>
                <div><strong>Starts:</strong> {formatDate(t.startDate)}</div>
                {t.endDate && <div><strong>Ends:</strong> {formatDate(t.endDate)}</div>}
                {t.maxOccurrences != null && <div><strong>Max occurrences:</strong> {t.maxOccurrences}</div>}
                <div><strong>Next run:</strong> {t.nextRunDate ? formatDate(t.nextRunDate) : '-'}</div>
                <div><strong>Mode:</strong> {t.mode === 'POST' ? 'Auto-post' : 'Draft'}</div>
                <div><strong>Status:</strong> <Chip size="small" label={humanizeEnum(t.status)} color={statusColor(t.status)} /></div>
                <div><strong>Processed:</strong> {t.occurrencesProcessed}</div>
                {t.createdByName && <div><strong>Created by:</strong> {t.createdByName}</div>}
                {t.sourceEntryId && (
                    <div><Link to={`/accounting/journal-entries/${t.sourceEntryId}`}>Source journal</Link></div>
                )}
            </div>

            {canManage && (
                <div data-help-section="accounting.recurringDetail.toolbar" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
                    <Button data-help="accounting.recurringDetail.action.runNow" variant="contained" disabled={busy || !canOperate || t.status !== 'ACTIVE'} onClick={runNow}>Run now</Button>
                    <Button data-help="accounting.recurringDetail.action.skipNext" variant="outlined" disabled={busy || !editable || !t.nextRunDate}
                        onClick={() => act(() => RecurringJournalService.skipNext(id), 'Next occurrence skipped', 'Failed to skip')}>
                        Skip next
                    </Button>
                    {t.status === 'ACTIVE' && (
                        <Button data-help="accounting.recurringDetail.action.pause" variant="outlined" disabled={busy}
                            onClick={() => act(() => RecurringJournalService.pause(id), 'Paused', 'Failed to pause')}>Pause</Button>
                    )}
                    {(t.status === 'PAUSED' || t.status === 'INACTIVE') && (
                        <Button data-help="accounting.recurringDetail.action.resume" variant="outlined" disabled={busy}
                            onClick={() => {
                                const due: { count: number; first?: string } = t.nextRunDate
                                    ? countDueOccurrences(t, t.nextRunDate, toLocalIsoDate())
                                    : { count: 0 };
                                if (due.count > 3 && !window.confirm(
                                    `Resuming will generate ${due.count} missed journals starting ${formatDate(due.first!)} (12 per run). Continue? (Use Skip next to drop some first.)`)) {
                                    return;
                                }
                                act(() => RecurringJournalService.resume(id), 'Resumed', 'Failed to resume');
                            }}>Resume</Button>
                    )}
                    {editable && canOperate && (
                        <Button data-help="accounting.recurringDetail.action.edit" variant="outlined" onClick={() => navigate(`/accounting/recurring-journals/${id}/edit`)}>Edit</Button>
                    )}
                    {t.status !== 'INACTIVE' && (
                        <Button data-help="accounting.recurringDetail.action.deactivate" variant="outlined" color="error" disabled={busy}
                            onClick={() => act(() => RecurringJournalService.deactivate(id), 'Deactivated', 'Failed to deactivate')}>
                            Deactivate
                        </Button>
                    )}
                    {runs.length === 0 && (
                        <Button data-help="accounting.recurringDetail.action.delete" variant="outlined" color="error" disabled={busy}
                            onClick={async () => {
                                try {
                                    await RecurringJournalService.remove(id);
                                    navigate('/accounting/recurring-journals');
                                } catch (err) {
                                    toast.showApiError(err, 'Failed to delete');
                                }
                            }}>
                            Delete
                        </Button>
                    )}
                </div>
            )}

            <h3 style={{ marginBottom: 12 }}>Entry lines (each run)</h3>
            <ScrollableTable>
                <table data-help-section="accounting.recurringDetail.lines" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640, marginBottom: 24 }}>
                    <thead>
                        <tr><th data-help="accounting.recurringDetail.lines.col.account" style={th}>Account</th><th data-help="accounting.recurringDetail.lines.col.debit" style={th}>Debit</th><th data-help="accounting.recurringDetail.lines.col.credit" style={th}>Credit</th>{hasOrders && <th data-help="accounting.recurringDetail.lines.col.order" style={th}>Order</th>}{hasDepartments && <th data-help="accounting.recurringDetail.lines.col.department" style={th}>Department</th>}{hasProjects && <th data-help="accounting.recurringDetail.lines.col.project" style={th}>Project</th>}<th data-help="accounting.recurringDetail.lines.col.description" style={th}>Description</th></tr>
                    </thead>
                    <tbody>
                        {t.lines.map((l, i) => (
                            <tr key={i}>
                                <td style={td}>{l.accountCode} {l.accountName}</td>
                                <td style={td}>{l.entryType === 'DEBIT' ? formatMoney(l.amount) : '-'}</td>
                                <td style={td}>{l.entryType === 'CREDIT' ? formatMoney(l.amount) : '-'}</td>
                                {hasOrders && (
                                    <td style={td} data-testid="line-order">
                                        {l.buyerOrderId
                                            ? <Link to={`/costing/orders/${l.buyerOrderId}`}>{orderNumbers[l.buyerOrderId] ?? l.buyerOrderId.slice(0, 8)}</Link>
                                            : '-'}
                                    </td>
                                )}
                                {hasDepartments && (
                                    <td style={td} data-testid="line-department">{/* B4-H */}
                                        {l.departmentId ? (departmentNames[l.departmentId] ?? l.departmentId.slice(0, 8)) : '-'}
                                    </td>
                                )}
                                {hasProjects && (
                                    <td style={td} data-testid="line-project">{/* P1-A project */}
                                        {l.projectId ? (projectNames[l.projectId] ?? l.projectId.slice(0, 8)) : '-'}
                                    </td>
                                )}
                                <td style={td}>{l.description || '-'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </ScrollableTable>

            <h3 style={{ marginBottom: 12 }}>Run history</h3>
            {runs.length === 0 ? <div>No runs yet.</div> : (
                <ScrollableTable>
                    <table data-help-section="accounting.recurringDetail.runs" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
                        <thead>
                            <tr><th data-help="accounting.recurringDetail.runs.col.scheduled" style={th}>Scheduled</th><th data-help="accounting.recurringDetail.runs.col.result" style={th}>Result</th><th data-help="accounting.recurringDetail.runs.col.journal" style={th}>Journal</th><th data-help="accounting.recurringDetail.runs.col.detail" style={th}>Detail</th><th style={th}></th></tr>
                        </thead>
                        <tbody>
                            {runs.map(r => (
                                <tr key={r.id}>
                                    <td style={td}>{formatDate(r.scheduledDate)}</td>
                                    <td style={td}><Chip size="small" label={humanizeEnum(r.status)} color={statusColor(r.status)} /></td>
                                    <td style={td}>
                                        {r.journalEntryId ? (
                                            <Link to={`/accounting/journal-entries/${r.journalEntryId}`}>
                                                {r.journalEntryNumber || 'Open'}{r.journalEntryStatus ? ` (${humanizeEnum(r.journalEntryStatus)})` : ''}
                                            </Link>
                                        ) : '-'}
                                    </td>
                                    <td style={{ ...td, color: r.status === 'FAILED' ? '#c62828' : undefined }}>
                                        {r.errorText || r.note || '-'}
                                    </td>
                                    <td style={td}>
                                        {canOperate && r.status === 'FAILED' && (
                                            <Button data-help="accounting.recurringDetail.action.retry" size="small" variant="outlined" disabled={busy}
                                                onClick={() => act(() => RecurringJournalService.retryRun(r.id), 'Retry attempted', 'Retry failed')}>
                                                Retry
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </ScrollableTable>
            )}
        </div>
    );
};

export default RecurringJournalDetail;
